import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

// Shared look for the dashboard filter bar (date range, brands, platforms,
// pillars): compact outlined controls that show "Label  Value".

export const FILTER_HEIGHT = 40;

export const filterInputSx: SxProps<Theme> = {
  height: FILTER_HEIGHT,
  fontSize: '0.875rem',
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    py: 0,
    pl: 1.5,
    minHeight: 'unset !important',
  },
  '& .MuiSelect-icon': { color: 'text.secondary' },
};

export const filterMenuProps = {
  PaperProps: {
    sx: { maxHeight: '70vh', width: 280, mt: 0.75 },
  },
};

export const selectAllItemSx: SxProps<Theme> = (theme) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRadius: 0,
  mx: 0,
  mb: 0.5,
});

export const optionItemSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 1,
  // Checkboxes already show selection, so selected rows stay untinted.
  '&.Mui-selected': { backgroundColor: 'transparent' },
  '&.Mui-selected:hover, &:hover': { backgroundColor: 'action.hover' },
  '&.Mui-selected.Mui-focusVisible': { backgroundColor: 'action.selected' },
  // The "Only" shortcut only shows on hover/focus to keep the list calm.
  '& .filter-only': { opacity: 0 },
  '&:hover .filter-only, &.Mui-focusVisible .filter-only': { opacity: 1 },
};

export const onlyButtonSx: SxProps<Theme> = (theme) => ({
  minWidth: 'auto',
  ml: 1,
  px: 1,
  py: 0.25,
  fontSize: '0.75rem',
  color: theme.palette.primary.main,
  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
});

export const FilterValue: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box component="span" sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
    <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.8125rem', fontWeight: 500, flexShrink: 0 }}>
      {label}
    </Box>
    <Box
      component="span"
      sx={{ color: 'text.primary', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {value}
    </Box>
  </Box>
);

/** "All", the single name, or "3 of 9" for a multi-select summary. */
export const summarizeSelection = (
  selected: string[],
  all: Array<{ id: string; name: string }>,
): string => {
  if (selected.length === 0) return 'None';
  if (all.length > 0 && selected.length >= all.length) return 'All';
  if (selected.length === 1) return all.find((o) => o.id === selected[0])?.name ?? selected[0];
  return `${selected.length} of ${all.length}`;
};

export const FilterLoading: React.FC<{ label: string }> = ({ label }) => (
  <Box
    sx={(theme) => ({
      height: FILTER_HEIGHT,
      display: 'flex',
      alignItems: 'center',
      px: 1.5,
      borderRadius: 1,
      border: `1px solid ${theme.palette.divider}`,
      bgcolor: 'background.paper',
      color: 'text.secondary',
      fontSize: '0.875rem',
    })}
  >
    Loading {label}...
  </Box>
);
